import { Capacitor } from '@capacitor/core';
import { useIonToast } from '@ionic/react';
import SequencePlayer from 'components/SequencePlayer';
import _ from 'lodash';
import { RunModel } from 'models/RunModel/RunModel';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'usehooks-ts';
import { getVideoFileUriByNameIfPresent } from '../../util';
import { logger } from 'services/Logger';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from 'util/constants';

interface VideoPlayerViewProps {
  record: RunModel;
  videoActive: boolean;
  onVideoToggle: () => void;
  hasVideo: boolean;
  hidden?: boolean;
  showRunStatsBox?: boolean;
  showFigurePopup?: boolean;
  showGForceDisplay?: boolean;
}
const VideoPlayerView = (props: VideoPlayerViewProps) => {
  const {
    record,
    hidden = false,
    hasVideo,
    videoActive,
    onVideoToggle,
    showRunStatsBox = true,
    showFigurePopup = true,
    showGForceDisplay = false,
  } = props;
  const { t } = useTranslation();
  const [present] = useIonToast();

  const [toast, dismiss] = useIonToast();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoSrc, setVideoSrc] = useState<string | undefined>(undefined);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [playTime, setPlayTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);

  useEffect(() => {
    if (hidden) return;
    if (!record.localVideoFileName) {
      present({
        message: 'Video not found',
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
      return;
    }
    getVideoFileUriByNameIfPresent(record.localVideoFileName).then((name) => {
      if (name) {
        let absPath = Capacitor.convertFileSrc(name);
        // Nur wenn sich der Pfad wirklich ändert, setze den State!
        setVideoSrc((prev) => (prev !== absPath ? absPath : prev));
      } else {
        present({
          message: 'Video not found',
          duration: MESSAGE_ALIVE_MS,
          color: 'danger',
          position: MESSAGE_POSITION,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.localVideoFileName, hidden]);

  const stopPlaying = () => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    setIsPlaying(false);
  };

  const togglePlaying = () => {
    if (!videoRef.current) return;
    if (videoRef.current?.paused) {
      if (duration - videoRef.current.currentTime < 0.011) {
        videoRef.current.currentTime = 0.001;
        setPlayTime(videoRef.current.currentTime);
      }
      videoRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(() => {
          /* ignore */
        });
    } else {
      stopPlaying();
    }
  };

  const onPlayBackError = (e: SyntheticEvent<HTMLVideoElement, Event>) => {
    logger.info('Video playback error:', e.type);
    present({
      message: 'Video could not be played',
      duration: MESSAGE_ALIVE_MS,
      color: 'danger',
      position: MESSAGE_POSITION,
    });
  };

  useInterval(
    () => {
      if (!isPlaying) return;
      const current = videoRef.current?.currentTime || 0;
      if (Math.abs(current - playTime) > 0.001) {
        setPlayTime(current);
      }
    },
    isPlaying ? 9 : null
  );

  const videoStyle: any = {
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    objectFit: 'cover',
    position: 'absolute',
    top: '0px',
  };

  return (
    <div
      style={{
        display: hidden ? 'none' : undefined,
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      {videoSrc && (
        <video
          // We append #t=0.001 to the videoSrc to show the first frame
          // while keeping the timer value at 0
          src={`${videoSrc}#t=0.001`}
          ref={videoRef}
          onDurationChange={() => setDuration(videoRef.current!.duration)}
          onError={onPlayBackError}
          autoPlay={false}
          onEnded={stopPlaying}
          playsInline
          style={videoStyle}
        />
      )}
      {!videoSrc && <div style={videoStyle}> Video has no source </div>}

      <SequencePlayer
        record={record}
        max={duration * 1000}
        step={0.1}
        value={playTime * 1000}
        isPlaying={isPlaying}
        onKnobMoveStart={stopPlaying}
        onChange={(value) => {
          if (!videoRef.current) return;
          const _value = Math.min(value / 1000, duration - 0.01);
          if (!isPlaying && playTime !== _value) {
            setPlayTime(_value);
            videoRef.current.currentTime = _value;
          }
        }}
        timeValue={formatTime(videoRef.current?.currentTime || 0)}
        togglePlaying={togglePlaying}
        toggleVideo={onVideoToggle}
        videoActive={videoActive}
        hasVideo={hasVideo}
        showRunStatsBox={showRunStatsBox}
        showFigurePopup={showFigurePopup}
        showGForceDisplay={showGForceDisplay}
      />
    </div>
  );
};

//A function that takes a float representing seconds and returns a string
// in the form of ss:ms
//
// Example:
// 1.5 -> '01:50'
// 0.5 -> '00:50'

const formatTime = (time: number): string => {
  const seconds = Math.floor(time);
  const milliseconds = Math.floor((time - seconds) * 100);
  return `${seconds.toString().padStart(2, '0')}:${milliseconds
    .toString()
    .padStart(2, '0')}`;
};

export default VideoPlayerView;
